export type PollConfig = {
  initialIntervalMs?: number;
  multiplier?: number;
  maxIntervalMs?: number;
  timeoutMs?: number;
};

const DEFAULTS: Required<PollConfig> = {
  initialIntervalMs: 500,
  multiplier: 1.5,
  maxIntervalMs: 3000,
  timeoutMs: 240_000, // large file batch processing (CSV/PDF) can take up to 4 min
};

export async function pollUntilDone<T>(
  pollFn: () => Promise<T>,
  isDone: (result: T) => boolean,
  signal?: AbortSignal,
  config?: PollConfig,
): Promise<T> {
  const { initialIntervalMs, multiplier, maxIntervalMs, timeoutMs } = { ...DEFAULTS, ...config };
  const deadline = Date.now() + timeoutMs;
  let interval = initialIntervalMs;

  /* eslint-disable no-await-in-loop -- sequential polling requires serial awaits */
  while (Date.now() < deadline) {
    if (signal?.aborted) {
      throw new DOMException('Polling aborted', 'AbortError');
    }

    const result = await pollFn();
    if (isDone(result)) {
      return result;
    }

    const remainingMs = deadline - Date.now();
    const waitMs = Math.min(interval, maxIntervalMs, remainingMs);
    if (waitMs <= 0) break;

    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(resolve, waitMs);
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new DOMException('Polling aborted', 'AbortError'));
        },
        { once: true },
      );
    });

    interval = Math.min(interval * multiplier, maxIntervalMs);
  }
  /* eslint-enable no-await-in-loop */

  throw new Error('Polling timed out waiting for a response');
}
